// src/fleet-stats/components/FleetBoard.jsx
//
// The centerpiece: every in-field robot as one chip in ONE flat grid, with
// exactly three visual states — needs attention (amber/red), driving (green),
// quiet (grey, dimmed). RRI details live in the tooltip and drill-down, not as
// a fifth color dimension. Offline robots are one summary line, not chips.
// Dataset-relative ("now" layer); deliberately does NOT follow the date filter.
import React, { useState } from "react";
import { Radio, Info } from "lucide-react";
import { C, CARD_BG, CARD_BORDER } from "../constants.js";
import { SectionHeader } from "./SharedUI.jsx";
import { fmtNum, fmtInt, displayRobotName } from "../utils/format.js";

const CHIP_CAP = 48;

function chipState(r) {
    if (r.attention === "critical") return { dot: C.red, border: `2px solid ${C.red}`, dim: false };
    if (r.attention === "warning") return { dot: C.amber, border: `2px solid ${C.amber}`, dim: false };
    if (r.active) return { dot: C.neonGreen, border: null, dim: false };
    return { dot: C.grey3, border: null, dim: true };
}

function RobotChip({ r, onSelectRobot }) {
    const s = chipState(r);
    const tooltip = [
        `${r.robotName} · ${r.client}${r.siteName ? ` / ${r.siteName}` : ""}`,
        r.lastDrove ? `last drove ${r.lastDrove}` : "no driving on record",
        r.rri !== null ? `RRI ${r.rri} (${r.rriLabel})` : "RRI unscored",
    ].join(" · ");
    return (
        <button
            onClick={() => onSelectRobot(r.robotName)}
            className="flex items-center gap-2.5 px-3 rounded-lg text-left transition-all"
            style={{
                backgroundColor: CARD_BG,
                border: CARD_BORDER,
                borderLeft: s.border || CARD_BORDER.replace("1px", "2px"),
                height: 48,
                opacity: s.dim ? 0.55 : 1,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)"; e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CARD_BG; e.currentTarget.style.opacity = s.dim ? 0.55 : 1; }}
            title={tooltip}
        >
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: s.dot, flexShrink: 0 }} />
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm" style={{ color: C.offWhite }}>
                    {displayRobotName(r.robotName, r.siteName)}
                </span>
                <span className="block truncate text-xs" style={{ color: C.grey2 }}>{r.client}</span>
            </span>
            <span className="text-xs tabular-nums shrink-0" style={{ color: r.active ? C.grey1 : C.grey2 }}>
                {r.active
                    ? `${fmtNum(r.km7d, 1)} km`
                    : r.daysQuiet !== null ? `${r.daysQuiet}d quiet` : "quiet"}
            </span>
        </button>
    );
}

function GroupHeader({ color, label, count }) {
    return (
        <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: C.grey2 }}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: color }} />
            {label}
            <span style={{ color: C.grey3 }}>({count})</span>
        </div>
    );
}

function ChipGroup({ group, onSelectRobot }) {
    const [expanded, setExpanded] = useState(false);
    const visible = expanded ? group.robots : group.robots.slice(0, CHIP_CAP);
    const hidden = group.robots.length - visible.length;
    return (
        <div>
            <GroupHeader color={group.color} label={group.label} count={group.robots.length} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(215px, 1fr))", gap: 8 }}>
                {visible.map((r) => (
                    <RobotChip key={r.robotId} r={r} onSelectRobot={onSelectRobot} />
                ))}
                {hidden > 0 && (
                    <button
                        onClick={() => setExpanded(true)}
                        className="flex items-center justify-center rounded-lg text-xs font-semibold transition-colors"
                        style={{ backgroundColor: "transparent", border: "1px dashed rgba(255,255,255,0.15)", color: C.grey2, height: 48 }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = C.grey2)}
                    >
                        +{hidden} more
                    </button>
                )}
            </div>
        </div>
    );
}

export function FleetBoard({ board, offlineRecentCount = 0, onSelectRobot, onShowOffline }) {
    // Explicit status groups — the boundaries ARE the legend
    const groups = [
        { key: "attention", label: "Needs attention", color: C.amber, robots: board.robots.filter((r) => r.attention) },
        { key: "drove", label: "Drove (last 7 days)", color: C.neonGreen, robots: board.robots.filter((r) => !r.attention && r.active) },
        { key: "quiet", label: "Quiet (powered on, not driving)", color: C.grey3, robots: board.robots.filter((r) => !r.attention && !r.active) },
    ].filter((g) => g.robots.length > 0);

    return (
        <div className="mb-10">
            <SectionHeader icon={Radio}>
                <span className="inline-flex items-center gap-2">
                    In the field
                    {board.latest && (
                        <>
                            <span className="relative inline-flex items-center group" style={{ cursor: "help" }}>
                                <Info size={14} style={{ color: C.grey2 }} />
                                <span
                                    className="pointer-events-none absolute left-0 top-full mt-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ backgroundColor: C.deepBlue, border: `1px solid ${C.grey3}`, borderRadius: 8, padding: "8px 12px", color: C.offWhite, fontSize: 12, fontWeight: 400, boxShadow: "0 4px 20px rgba(0,0,0,0.4)", zIndex: 50, width: 300 }}
                                >
                                    In-field robots: any data within the last week of the dataset. "Drove" means driving activity in the last 7 days (ending {board.latest}); km shown is the 7-day total. Not real-time — the data is a daily batch. Hover a robot for its readiness score; click to drill down.
                                </span>
                            </span>
                            <span
                                className="ml-1 inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-md"
                                style={{ backgroundColor: "rgba(255,255,255,0.04)", color: C.grey2, border: "1px solid rgba(255,255,255,0.08)" }}
                            >
                                {board.cutoff} → {board.latest}
                            </span>
                        </>
                    )}
                </span>
            </SectionHeader>

            {board.robots.length === 0 ? (
                <div className="rounded-xl px-4 py-3" style={{ backgroundColor: CARD_BG, border: CARD_BORDER, color: C.grey2 }}>
                    No in-field robots in this scope.
                </div>
            ) : (
                <div className="flex flex-col gap-5">
                    {groups.map((g) => (
                        <ChipGroup key={g.key} group={g} onSelectRobot={onSelectRobot} />
                    ))}
                </div>
            )}

            {board.offlineTotal > 0 && (
                <button
                    onClick={onShowOffline}
                    className="mt-3 text-xs transition-colors"
                    style={{ color: C.grey2 }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = C.grey2)}
                >
                    {fmtInt(board.offlineTotal)} more robots are offline (no data in over a week)
                    {offlineRecentCount > 0 ? ` — ${fmtInt(offlineRecentCount)} of them went dark in the last 30 days` : ""} → view
                </button>
            )}
        </div>
    );
}
